import { db } from '../firebase';
import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  deleteDoc,
  addDoc,
  updateDoc,
  collection,
  query,
  where,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';

// Read a user's public profile
export async function getUserProfile(uid) {
  const ref = doc(db, 'users', uid, 'profile', 'public');
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

// Save/create user profile
export async function saveUserProfile(uid, data) {
  const ref = doc(db, 'users', uid, 'profile', 'public');
  await setDoc(ref, {
    ...data,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

// Create default profile from Firebase Auth user object
export function createDefaultProfile(user) {
  return {
    displayName: user.displayName || '',
    photoURL: user.photoURL || null,
    username: '',
    bio: '',
    location: '',
    instruments: [],
    genres: [],
    visibility: 'private',
    tuneVisibility: 'none',
    setVisibility: 'none',
    publicTuneIds: [],
    publicSetIds: [],
  };
}

// Validate username format
export function isValidUsername(username) {
  return /^[a-zA-Z0-9_-]{3,20}$/.test(username);
}

// Check if a username is available
export async function isUsernameAvailable(username) {
  const ref = doc(db, 'usernames', username.toLowerCase());
  const snap = await getDoc(ref);
  return !snap.exists();
}

// Claim a username (atomic transaction to prevent duplicates)
export async function claimUsername(uid, newUsername, oldUsername) {
  const newRef = doc(db, 'usernames', newUsername.toLowerCase());

  await runTransaction(db, async (transaction) => {
    const newSnap = await transaction.get(newRef);
    if (newSnap.exists() && newSnap.data().uid !== uid) {
      throw new Error('Username is already taken');
    }

    // Release old username if changing
    if (oldUsername && oldUsername.toLowerCase() !== newUsername.toLowerCase()) {
      const oldRef = doc(db, 'usernames', oldUsername.toLowerCase());
      transaction.delete(oldRef);
    }

    // Claim new username
    transaction.set(newRef, { uid, claimedAt: serverTimestamp() });
  });
}

// Release a username
export async function releaseUsername(username) {
  if (!username) return;
  await deleteDoc(doc(db, 'usernames', username.toLowerCase()));
}

// Resolve a username to a UID
export async function resolveUsername(username) {
  const ref = doc(db, 'usernames', username.toLowerCase());
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data().uid;
  return null;
}

// Follow a user (stores by UID so name changes don't matter)
// genres: optional array — empty/undefined = follow all genres, populated = only those genres
export async function followUser(myUid, targetUid, genres) {
  const ref = doc(db, 'users', myUid, 'following', targetUid);
  await setDoc(ref, { followedAt: serverTimestamp(), genres: genres || [] });
}

// Unfollow a user
export async function unfollowUser(myUid, targetUid) {
  const ref = doc(db, 'users', myUid, 'following', targetUid);
  await deleteDoc(ref);
}

// Check if I follow a user
export async function isFollowing(myUid, targetUid) {
  const ref = doc(db, 'users', myUid, 'following', targetUid);
  const snap = await getDoc(ref);
  return snap.exists();
}

// Get the full follow document (includes genres preferences)
export async function getFollowDoc(myUid, targetUid) {
  const ref = doc(db, 'users', myUid, 'following', targetUid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

// Update which genres I follow for a user
export async function updateFollowGenres(myUid, targetUid, genres) {
  const ref = doc(db, 'users', myUid, 'following', targetUid);
  await updateDoc(ref, { genres: genres || [] });
}

// Get list of UIDs I follow
export async function getFollowing(myUid) {
  const snap = await getDocs(collection(db, 'users', myUid, 'following'));
  return snap.docs.map(d => d.id);
}

// Get public tunes for a user based on their profile visibility settings
export async function getPublicTunes(uid, profile) {
  if (!profile || profile.visibility !== 'public') return [];
  if (profile.tuneVisibility === 'none') return [];

  if (profile.tuneVisibility === 'all') {
    const snap = await getDocs(collection(db, 'users', uid, 'tunes'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  if (profile.tuneVisibility === 'byGenre' && Array.isArray(profile.publicTuneGenres)) {
    const snap = await getDocs(collection(db, 'users', uid, 'tunes'));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(t => profile.publicTuneGenres.includes(t.genre));
  }

  if (profile.tuneVisibility === 'selected' && Array.isArray(profile.publicTuneIds)) {
    const results = [];
    for (const tuneId of profile.publicTuneIds) {
      const tuneSnap = await getDoc(doc(db, 'users', uid, 'tunes', tuneId));
      if (tuneSnap.exists()) {
        results.push({ id: tuneSnap.id, ...tuneSnap.data() });
      }
    }
    return results;
  }

  return [];
}

// Get public sets for a user based on their profile visibility settings
export async function getPublicSets(uid, profile) {
  if (!profile || profile.visibility !== 'public') return [];
  if (profile.setVisibility === 'none') return [];

  if (profile.setVisibility === 'all') {
    const snap = await getDocs(collection(db, 'users', uid, 'sets'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  if (profile.setVisibility === 'byGenre' && Array.isArray(profile.publicSetGenres)) {
    // Sets don't have a top-level genre — derive from component tunes
    const [setsSnap, tunesSnap] = await Promise.all([
      getDocs(collection(db, 'users', uid, 'sets')),
      getDocs(collection(db, 'users', uid, 'tunes'))
    ]);
    const tuneMap = {};
    tunesSnap.docs.forEach(d => { tuneMap[d.id] = d.data(); });

    return setsSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(s => {
        const setGenres = (s.tunes || []).map(t => tuneMap[t.id]?.genre).filter(Boolean);
        return setGenres.some(g => profile.publicSetGenres.includes(g));
      });
  }

  if (profile.setVisibility === 'selected' && Array.isArray(profile.publicSetIds)) {
    const results = [];
    for (const setId of profile.publicSetIds) {
      const setSnap = await getDoc(doc(db, 'users', uid, 'sets', setId));
      if (setSnap.exists()) {
        results.push({ id: setSnap.id, ...setSnap.data() });
      }
    }
    return results;
  }

  return [];
}

// --- Notifications ---

// Send a tune to another user
export async function sendTuneToUser(senderUid, senderProfile, recipientUid, tuneId, tuneTitle) {
  await addDoc(collection(db, 'users', recipientUid, 'notifications'), {
    type: 'tune_send',
    senderUid,
    senderDisplayName: senderProfile?.displayName || '',
    senderUsername: senderProfile?.username || '',
    tuneId,
    tuneTitle: tuneTitle || 'Untitled',
    status: 'pending',
    createdAt: serverTimestamp(),
    readAt: null
  });
}

// Send a set (with its tunes) to another user
export async function sendSetToUser(senderUid, senderProfile, recipientUid, setId, setTitle, tuneIds) {
  await addDoc(collection(db, 'users', recipientUid, 'notifications'), {
    type: 'set_send',
    senderUid,
    senderDisplayName: senderProfile?.displayName || '',
    senderUsername: senderProfile?.username || '',
    setId,
    setTitle: setTitle || 'Untitled Set',
    tuneIds: tuneIds || [],
    status: 'pending',
    createdAt: serverTimestamp(),
    readAt: null
  });
}

// Send a session log to another user
export async function sendSessionToUser(senderUid, senderProfile, recipientUid, sessionId, sessionTitle, sessionTunes) {
  await addDoc(collection(db, 'users', recipientUid, 'notifications'), {
    type: 'session_send',
    senderUid,
    senderDisplayName: senderProfile?.displayName || '',
    senderUsername: senderProfile?.username || '',
    sessionId,
    sessionTitle: sessionTitle || 'Session',
    sessionTunes: sessionTunes || [],
    status: 'pending',
    createdAt: serverTimestamp(),
    readAt: null
  });
}

// Send a batch of tunes/sets to another user
export async function sendBatchToUser(senderUid, senderProfile, recipientUid, items) {
  await addDoc(collection(db, 'users', recipientUid, 'notifications'), {
    type: 'batch_send',
    senderUid,
    senderDisplayName: senderProfile?.displayName || '',
    senderUsername: senderProfile?.username || '',
    items: items || [],
    status: 'pending',
    createdAt: serverTimestamp(),
    readAt: null
  });
}

// Get pending notifications
export async function getNotifications(uid) {
  const q = query(
    collection(db, 'users', uid, 'notifications'),
    where('status', '==', 'pending')
  );
  const snap = await getDocs(q);
  const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Sort client-side (newest first) to avoid needing a composite index
  results.sort((a, b) => {
    const aTime = a.createdAt?.toMillis?.() || 0;
    const bTime = b.createdAt?.toMillis?.() || 0;
    return bTime - aTime;
  });
  return results;
}

// Count unread notifications (for badge)
export async function getUnreadNotificationCount(uid) {
  const q = query(
    collection(db, 'users', uid, 'notifications'),
    where('status', '==', 'pending')
  );
  const snap = await getDocs(q);
  // Filter client-side to avoid composite index requirement
  return snap.docs.filter(d => !d.data().readAt).length;
}

// Update notification status
export async function updateNotificationStatus(uid, notifId, newStatus) {
  await updateDoc(doc(db, 'users', uid, 'notifications', notifId), { status: newStatus });
}

// Mark notifications as read
export async function markNotificationsRead(uid, notifIds) {
  for (const nid of notifIds) {
    await updateDoc(doc(db, 'users', uid, 'notifications', nid), { readAt: serverTimestamp() });
  }
}
